import { Component, ElementRef, HostBinding, Input, OnChanges, ViewChild } from '@angular/core';

import { IPresenter } from 'rev-shared/webrtc/Contract';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';
import { identity } from 'rev-shared/util';
import { lazyHtml2canvas } from 'rev-shared/util/lazyHtml2Canvas';

import { IProducerBgImage, WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';

import { IProducerLayout, IProducerLayoutVideo, IProducerOptions } from '../../Contract';
import { getDefaultBgImgUrl, ProducerService } from '../../Producer.Service';

import styles from './VideoPreview.module.less';

@Component({
	selector: 'video-preview-player',
	templateUrl: './VideoPreviewPlayer.Component.html',
	host: {
		'[class]': 'styles.videoPreviewPlayer'
	},
	standalone: false
})
export class VideoPreviewPlayerComponent implements OnChanges {
	@Input() public layoutCfg: IProducerLayout;
	@Input() public bgImages: IProducerBgImage[];
	@Input() public isPreview: boolean;
	@Input() public noStreamText: string;
	@Input() public isProducer: boolean;
	@Input() public producerOptions: IProducerOptions;

	public readonly identity = identity;
	public readonly styles = styles;

	public bgUrl: string;

	@ViewChild('innerContent')
	public innerContentEl: ElementRef;

	@HostBinding('class.editable')
	public get canEdit() {
		return this.isProducer && this.isPreview;
	}

	constructor(
		private readonly ProducerService: ProducerService,
		private readonly ListenerConnection: WebRtcListenerConnectionService
	) {}

	public ngOnChanges(changes): void {
		if(changes.layoutCfg) {
			this.bgUrl = this.getBgUrl();
		}
	}

	public getBgUrl(): string {
		const id = this.layoutCfg.backgroundId;
		if(!id) {
			return;
		}
		if(!this.layoutCfg.isCustomBg && this.producerOptions.deletedDefaultBgs.every(bg => bg !== id)) {
			return getDefaultBgImgUrl(id);
		}

		return this.bgImages.find(img => img.id === id)?.original;
	}

	public drop(data: IProducerLayoutVideo & { index: number }, layoutIndex: number): void {
		const area = this.layoutCfg.videos[layoutIndex];

		if(!area || !data || !this.canEdit || !this.isPreview || data.index === layoutIndex) {
			console.log('VideoPreviewPlayer.drop ignoring');
			return;
		}

		if(data.index >= 0) {
			this.ProducerService.swapPreviewSlots(data.index, layoutIndex)
				.catch(e => console.error('VideoPreviewPlayer. swapPreviewSlots: ', e));
			return;
		}

		this.ProducerService.addPresenterToPreview(data, layoutIndex)
			.catch(e => console.error('VideoPreviewPlayer. addPresenterToPreview: ', e));
	}

	public dropInitialVideo(video: IProducerLayoutVideo) {
		this.ProducerService.initLayout(video)
			.catch(e => console.error('VideoPreviewPlayer. addPresenterToPreview: ', e));
	}

	public getPresenter(id: string): IPresenter {
		return this.ListenerConnection.presenters.getPresenter(id);
	}

	public getScreenshot(): Promise<HTMLCanvasElement> {
		return lazyHtml2canvas(this.innerContentEl.nativeElement, {
			backgroundColor: '#000',
			scale: 1,
			useCORS: true
		});
	}
}
